import { useMutation } from "@tanstack/react-query";
import {
    RecognizeScheduleMilestoneBody,
    RecognizeScheduleMilestoneResponse,
    RecognizeScheduleMilestonesBulkBody,
    RecognizeScheduleMilestonesBulkResponse,
} from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import { useScope } from "@state/scopeContext.lib";
import { extractApiErrorMessage, getApiErrorMessage } from "./apiError";
import {
    getScheduleMilestoneAlreadyPostedError,
    ScheduleMilestoneAlreadyRecognizedError,
    ScheduleMilestoneRefusedError,
    SchedulePeriodLockedError,
} from "./scheduleErrors";
import {
    invalidateAfterLifecycleAction,
    requestScheduleAction,
    scheduleTemplateClient,
    SUPPRESS_ERROR_TOAST_HEADERS,
} from "./scheduleTemplate.api";

export interface RecognizeScheduleMilestoneRequest extends RecognizeScheduleMilestoneBody {
    applicationId: string;
    sortOrder: number;
}

export const SCHEDULE_MILESTONE_RECOGNIZE_FAILED_MESSAGE =
    "Failed to recognize the milestone";

export async function recognizeScheduleMilestone(
    options: RecognizeScheduleMilestoneRequest & {
        workspaceId: string | null | undefined;
    },
): Promise<RecognizeScheduleMilestoneResponse> {
    const response = await requestScheduleAction(
        () =>
            scheduleTemplateClient.recognizeMilestone({
                extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                params: {
                    workspaceId: options.workspaceId ?? "",
                    id: options.applicationId,
                    sortOrder: options.sortOrder,
                },
                body: {
                    entityId: options.entityId,
                    postingDate: options.postingDate,
                },
            }),
        SCHEDULE_MILESTONE_RECOGNIZE_FAILED_MESSAGE,
        (error) => {
            const alreadyPosted = getScheduleMilestoneAlreadyPostedError(error);
            if (alreadyPosted) {
                return new ScheduleMilestoneAlreadyRecognizedError(
                    alreadyPosted,
                );
            }
            const status = error.response?.status;
            return status === 409 || status === 404
                ? new ScheduleMilestoneRefusedError(
                      getApiErrorMessage(
                          error,
                          SCHEDULE_MILESTONE_RECOGNIZE_FAILED_MESSAGE,
                      ),
                  )
                : null;
        },
    );
    if (response.status !== 200) {
        throw new Error(
            extractApiErrorMessage(
                response.body,
                SCHEDULE_MILESTONE_RECOGNIZE_FAILED_MESSAGE,
            ),
        );
    }
    return response.body;
}

export function useRecognizeScheduleMilestoneMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (options: RecognizeScheduleMilestoneRequest) =>
            await recognizeScheduleMilestone({ ...options, workspaceId }),
        onSuccess: () => invalidateAfterLifecycleAction(workspaceId),
        onError: (error) => {
            // A locked month posts nothing, so the cache is still right.
            if (!(error instanceof SchedulePeriodLockedError)) {
                invalidateAfterLifecycleAction(workspaceId);
            }
        },
    });
}

export const SCHEDULE_MILESTONES_RECOGNIZE_BULK_FAILED_MESSAGE =
    "Failed to recognize milestones";

export async function recognizeScheduleMilestonesBulk(
    options: RecognizeScheduleMilestonesBulkBody & {
        workspaceId: string | null | undefined;
    },
): Promise<RecognizeScheduleMilestonesBulkResponse> {
    const response = await requestScheduleAction(
        () =>
            scheduleTemplateClient.recognizeMilestonesBulk({
                extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                params: { workspaceId: options.workspaceId ?? "" },
                body: {
                    entityId: options.entityId,
                    postingDate: options.postingDate,
                    milestones: options.milestones,
                },
            }),
        SCHEDULE_MILESTONES_RECOGNIZE_BULK_FAILED_MESSAGE,
    );
    if (response.status !== 200) {
        throw new Error(
            extractApiErrorMessage(
                response.body,
                SCHEDULE_MILESTONES_RECOGNIZE_BULK_FAILED_MESSAGE,
            ),
        );
    }
    return response.body;
}

/** Every row posts (or fails) on its own, so the cache is stale either way. */
export function useRecognizeScheduleMilestonesBulkMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (options: RecognizeScheduleMilestonesBulkBody) =>
            await recognizeScheduleMilestonesBulk({ ...options, workspaceId }),
        onSettled: () => invalidateAfterLifecycleAction(workspaceId),
    });
}
